/* =============================================================================
   Car Verif — Precheck v2 (/pages/verification)
   -----------------------------------------------------------------------------
   Choreographed verification sequence between plate submit and checkout.

   Honesty contract (principe 3 — prouver, pas prétendre):
     - Row 1 "Interrogation SIV" is a REAL live lookup. On load the page
       fires POST /prefetch (queues a real identification server-side when
       the plate is not cached), then polls GET /lookup every ~4 s for up
       to 75 s. "pending" keeps row 1 running with honest microcopy.
     - Rows 2-6 are COVERAGE checks — phrased as coverage availability,
       never as completed analysis. The full analysis happens after purchase.
     - Timestamps are real elapsed times of this page's own sequence.
     - No fabricated findings, no fake counters, no fake urgency.

   Conversion is never held hostage to the scrape: the email field + CTA
   unlock when the ~6,5 s choreography completes, whatever row 1 is doing.
   If the vehicle is identified after the generic card is already shown,
   the card upgrades in place (height animated — the email field and CTA
   below never jump under the visitor's focus or thumb).

   Checkout flow: the buy form POSTs /cart/add and is intercepted by the
   global cv-v2-plate.js handler (cart clear → add → return_to). This file
   only prepares the form (plate property, _source, return_to email prefill)
   BEFORE the submit event fires, and fires the lead beacon fire-and-forget.
   Submitting while row 1 is still pending just proceeds — the prefetch
   continues server-side and warms the cache regardless.

   QA hooks (?mock=found|generic|pending-found) short-circuit the network:
   no prefetch, no lookup. They never fabricate anything for real visitors —
   they only activate via explicit URL param.

   No libraries. Defer-loaded. Idempotent.
   ============================================================================= */

(function () {
  'use strict';

  if (window.__cvV2PrecheckBooted) return;
  window.__cvV2PrecheckBooted = true;

  var LOOKUP_URL = 'https://precheck.carverif.fr/lookup';
  var PREFETCH_URL = 'https://precheck.carverif.fr/prefetch';
  var LEAD_URL = 'https://precheck.carverif.fr/lead';
  var BEACON_URL = 'https://carverif-wheel-beacon.walid-pro.workers.dev/e';

  /* A/B « révélation ». A = plancher fixe (comportement historique, contrôle).
     B = on révèle dès que l'identification aboutit, plafonnée à REVEAL_CAP_MS.
     Le bras est stable par visiteur ; ?ab=A|B le force (QA). */
  var AB_MEMORY = null;
  function pickArm() { return Math.random() < 0.5 ? 'A' : 'B'; }
  function abReveal() {
    try {
      var q = (window.location.search.match(/[?&]ab=(A|B)\b/) || [])[1];
      if (q) return q;
    } catch (e) { /* ignore */ }
    try {
      var v = window.localStorage.getItem('cv_ab_reveal');
      if (v !== 'A' && v !== 'B') { v = pickArm(); window.localStorage.setItem('cv_ab_reveal', v); }
      return v;
    } catch (e) { /* stockage refusé */ }
    try {
      var w = window.sessionStorage.getItem('cv_ab_reveal');
      if (w !== 'A' && w !== 'B') { w = pickArm(); window.sessionStorage.setItem('cv_ab_reveal', w); }
      return w;
    } catch (e) { /* ignore */ }
    if (!AB_MEMORY) AB_MEMORY = pickArm();
    return AB_MEMORY;
  }
  var LOOKUP_TIMEOUT_MS = 3000; // per polling request
  var PREFETCH_TIMEOUT_MS = 3000;
  var LEAD_TIMEOUT_MS = 2000;
  var POLL_SCHEDULE_MS = [700, 1200, 1800, 2500, 3000]; // fast early burst — cached plates feel instant
  var POLL_INTERVAL_MS = 3500; // steady cadence after the burst
  // Live-interrogation budget. Measured real scrapes 2026-07-20: avg 51 s,
  // p90 ~70 s, slowest 94 s — a 75 s window settled to "generic" on a plate
  // the SIV had actually identified. 130 s covers the tail, under the
  // server-side scrape timeout (150 s).
  var POLL_WINDOW_MS = 130000; // total live-interrogation budget
  /* Révélation de l'offre. Le plancher laisse la chorégraphie se dérouler ;
     le plafond garantit qu'on ne fait jamais attendre indéfiniment. Mesuré sur
     4 060 scrapes réels : p50 6,8 s, p75 12,5 s, p90 18,3 s — à 6,4 s seuls
     21,8 % des véhicules sont identifiés, contre 87,6 % à 15 s. */
  var REVEAL_FLOOR_MS = 6400;
  var REVEAL_CAP_MS = 25000;

  /* --- Plate helpers — delegate to cv-v2-plate.js, local fallback --------- */

  var SIV_STRICT = /^[A-HJ-NP-TV-Z]{2}[0-9]{3}[A-HJ-NP-TV-Z]{2}$/;
  var FNI = /^[0-9]{1,4}[A-Z]{1,3}(?:0[1-9]|[1-8][0-9]|9[0-5]|2[AB]|97[1-6])$/;

  function strip(raw) {
    if (window.cvV2Plate) return window.cvV2Plate.strip(raw);
    return (raw || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  }

  function isValidPlate(norm) {
    if (window.cvV2Plate) return window.cvV2Plate.isValid(norm);
    return SIV_STRICT.test(norm) || FNI.test(norm);
  }

  function canonical(norm) {
    if (window.cvV2Plate) return window.cvV2Plate.canonical(norm);
    if (SIV_STRICT.test(norm)) {
      return norm.slice(0, 2) + '-' + norm.slice(2, 5) + '-' + norm.slice(5);
    }
    var m = norm.match(/^([0-9]{1,4})([A-Z]{1,3})([0-9A-B]+)$/);
    if (m) return m[1] + ' ' + m[2] + ' ' + m[3];
    return norm;
  }

  /* --- Small utils --------------------------------------------------------- */

  function delay(ms) {
    return new Promise(function (resolve) { setTimeout(resolve, ms); });
  }

  // French elapsed-seconds format: 2,3 s (comma decimal, nbsp before unit)
  function fmtElapsed(ms) {
    return (ms / 1000).toFixed(1).replace('.', ',') + ' s';
  }

  function cleanField(v, max) {
    if (typeof v !== 'string') return '';
    return v.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim().slice(0, max || 40);
  }

  /* --- Marque logo --------------------------------------------------------
     cvSlugifyMarque is the SINGLE canonical normalization; it is mirrored
     byte-for-byte in car-logos/build_logos.py, which named the shipped
     cv-logo-<slug>.png assets. Changing one without the other breaks the
     lookup. A marque that resolves to no asset simply renders no logo. */
  var CV_LOGO_ALIASES = {
    'b-m-w': 'bmw',
    'bmw-i': 'bmw',
    'mercedes': 'mercedes-benz',
    'vw': 'volkswagen',
    'alfa': 'alfa-romeo',
    'land': 'land-rover',
    'ds-automobiles': 'ds',
    'lynk-&-co': 'lynk-and-co'
  };

  function cvSlugifyMarque(marque) {
    var s = String(marque == null ? '' : marque).trim().toUpperCase();
    try { s = s.normalize('NFKD'); } catch (e) { /* older engines: skip */ }
    s = s.replace(/[^\x20-\x7E]/g, '');          // drop diacritics + non-ASCII
    s = s.replace(/[.\s]+/g, '-')                // dots/spaces -> hyphen
         .replace(/-+/g, '-')
         .replace(/^-+|-+$/g, '')
         .toLowerCase();
    return CV_LOGO_ALIASES[s] || s;
  }

  /* Map emitted by main-precheck-v2.liquid. Parsed once, lazily; any failure
     degrades to "no logo" rather than throwing inside the found handler. */
  var cvLogoMapCache;
  function cvLogoUrl(marque) {
    if (cvLogoMapCache === undefined) {
      cvLogoMapCache = null;
      try {
        var el = document.getElementById('cv-logo-map');
        if (el) cvLogoMapCache = JSON.parse(el.textContent || 'null');
      } catch (e) { cvLogoMapCache = null; }
    }
    if (!cvLogoMapCache) return '';
    var slug = cvSlugifyMarque(marque);
    return (slug && cvLogoMapCache[slug]) || '';
  }

  /* --- Boot ---------------------------------------------------------------- */

  function boot() {
    var root = document.querySelector('[data-cv-precheck]');
    if (!root) return;

    var refs = {
      entry: root.querySelector('[data-cv-pc-entry]'),
      entryForm: root.querySelector('[data-cv-pc-entry-form]'),
      entryInput: root.querySelector('[data-cv-pc-entry-input]'),
      run: root.querySelector('[data-cv-pc-run]'),
      chip: root.querySelector('.cv-v2-precheck__chip'),
      chipChars: root.querySelector('[data-cv-pc-chip-chars]'),
      chipBadge: root.querySelector('[data-cv-pc-chip-badge]'),
      chipEdit: root.querySelector('[data-cv-pc-chip-edit]'),
      listWrap: root.querySelector('[data-cv-pc-list-wrap]'),
      count: root.querySelector('[data-cv-pc-count]'),
      countN: root.querySelector('[data-cv-pc-count-n]'),
      ticker: root.querySelector('[data-cv-pc-ticker]'),
      tickerFact: root.querySelector('[data-cv-pc-ticker-fact]'),
      status: root.querySelector('[data-cv-pc-status]'),
      statusText: root.querySelector('[data-cv-pc-status-text]'),
      statusTime: root.querySelector('[data-cv-pc-status-time]'),
      rows: root.querySelectorAll('[data-cv-pc-row]'),
      intent: root.querySelector('[data-cv-pc-intent]'),
      intentChips: root.querySelectorAll('[data-cv-pc-intent-chip]'),
      result: root.querySelector('[data-cv-pc-result]'),
      foundCard: root.querySelector('[data-cv-pc-found]'),
      genericCard: root.querySelector('[data-cv-pc-generic]'),
      vehicle: root.querySelector('[data-cv-pc-vehicle]'),
      vehicleMeta: root.querySelector('[data-cv-pc-vehicle-meta]'),
      logo: root.querySelector('[data-cv-pc-logo]'),
      vehicleProp: root.querySelector('[data-cv-pc-vehicle-prop]'),
      genericPlate: root.querySelector('[data-cv-pc-generic-plate]'),
      buy: root.querySelector('[data-cv-pc-buy]'),
      email: root.querySelector('[data-cv-pc-email]'),
      cta: root.querySelector('[data-cv-pc-cta]'),
      plateProp: root.querySelector('[data-cv-pc-plate-prop]'),
      sourceProp: root.querySelector('[data-cv-pc-source-prop]'),
      returnTo: root.querySelector('[data-cv-pc-return]')
    };

    var params = new URLSearchParams(window.location.search);
    var reduced = false;
    try {
      reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) { /* ignore */ }

    // QA hook — ?mock=found|generic|pending-found short-circuits the network
    // (lookup AND prefetch) so the page can be exercised without triggering
    // a real identification. pending-found: pending 12 s, then found. Never
    // fabricates anything for real visitors: explicit URL param only.
    var mock = params.get('mock');

    var MOCK_FOUND = { status: 'found', marque: 'RENAULT', modele: 'MÉGANE III', annee: '2012', energie: 'Diesel' };

    // _source: incoming ?src= (sanitized) or "precheck"
    var src = cleanField(params.get('src') || '', 48).replace(/[^\w:.-]/g, '');
    if (!src) src = 'precheck';
    if (refs.sourceProp) refs.sourceProp.value = src;

    var intentValue = null;
    var startedAt = 0;
    var lastLeadKey = null;

    // Live-lookup state machine: 'pending' until the poll settles.
    var lookupState = 'pending'; // 'pending' | 'found' | 'generic'
    var foundRes = null;
    var resultShown = false; // choreography done, result + buy visible
    var pendingNoted = false;
    var pcResultBeaconed = false; // one-shot guard for the precheck_result funnel beacon
    var arm = abReveal();         // 'A' (plancher fixe) | 'B' (révélation adaptative)
    var revealDone = false;       // showResult n'est jouée qu'une fois
    var revealTimer = null;
    var revealPlate = null;
    var revealFloorMs = 0;
    var revealCapMs = 0;
    var settledBeaconed = false;  // one-shot pour l'état terminal
    var startBeaconed = false;    // one-shot — dénominateur non biaisé
    var exitBeaconed = false;     // one-shot — abandon pendant l'attente
    var skipBtn = null;           // « Continuer sans attendre »

    var PENDING_NOTE = 'interrogation en temps réel — jusqu\'à une minute';

    /* Émission d'un évènement de mesure. Fire-and-forget, ne bloque jamais. */
    function beacon(event, extra) {
      try {
        if (!navigator.sendBeacon) return;
        var payload = { event: event, src: src };
        if (extra) {
          for (var k in extra) {
            if (Object.prototype.hasOwnProperty.call(extra, k)) payload[k] = extra[k];
          }
        }
        navigator.sendBeacon(BEACON_URL, JSON.stringify(payload));
      } catch (e) { /* ignore */ }
    }

    /* État terminal de l'identification : c'est CE beacon qui dit la vérité.
       precheck_result ne photographie que l'instant de la révélation et ne voit
       donc jamais les bascules tardives en « found ». */
    function beaconSettled() {
      if (settledBeaconed) return;
      settledBeaconed = true;
      var secs = Math.round((performance.now() - startedAt) / 1000);
      if (secs < 0) secs = 0;
      if (secs > 60) secs = 60;
      beacon('precheck_settled', { route: lookupState, ab: arm, ms: String(secs) });
    }

    /* Avance la révélation quand l'identification est retombée (bras B seul).
       Jamais sous le plancher : la chorégraphie doit avoir eu le temps de jouer. */
    function requestReveal() {
      if (revealDone || reduced || arm !== 'B' || !revealPlate) return;
      var wait = Math.max(0, revealFloorMs - (performance.now() - startedAt));
      if (revealTimer) clearTimeout(revealTimer);
      revealTimer = setTimeout(function () { showResult(revealPlate); }, wait);
    }

    /* Long-wait theater: rotating process lines (row 1) + fact ticker.
       Facts are REAL corpus stats (jq-verified 2026-07-19, 44 reports);
       the 12 935,91 € figure is confirmed in the source report PDF. */
    var ROTATE_MS = 7000;
    var PENDING_LINES = null; // built in startRun (needs the plate)
    var pendingLineIdx = 0;
    var pendingRotTimer = null;
    var tickerTimer = null;
    var tickerIdx = 0;
    var theaterGen = 0; // invalidates in-flight text swaps on stopTheater
    var doneRows = 0;
    var foundEntranceDone = false;

    var TICKER_FACTS = [
      '3 reculs de compteur avérés',
      '26 rapports sur 44 : usage société ou démonstration détecté (59 %)',
      'Un sinistre récent : 12 935,91 € de réparations expertisées',
      '8 rapports sur 44 notés 4/10 ou moins (18 %)',
      '12 rapports sur 44 : verdict « achat uniquement avec preuves »',
      '34 rapports sur 44 : reprise professionnelle dans l\'historique (77 %)',
      '15 rapports sur 44 : trou documentaire relevé (34 %)',
      '13 rapports sur 44 : un seul relevé kilométrique connu (30 %)',
      '7 rapports sur 44 : véhicule importé (16 %)'
    ];

    function swapText(el, txt) {
      if (reduced) { el.textContent = txt; return; }
      var gen = theaterGen;
      el.classList.add('cv-pc-swap-out');
      setTimeout(function () {
        el.classList.remove('cv-pc-swap-out');
        if (gen !== theaterGen) return; // theater stopped mid-swap — keep final text
        el.textContent = txt;
        el.classList.add('cv-pc-swap-in');
        setTimeout(function () { el.classList.remove('cv-pc-swap-in'); }, 240);
      }, 150);
    }

    function stopTheater() {
      theaterGen += 1;
      if (pendingRotTimer) { clearInterval(pendingRotTimer); pendingRotTimer = null; }
      if (tickerTimer) { clearInterval(tickerTimer); tickerTimer = null; }
    }

    function startTicker() {
      if (reduced || tickerTimer || !refs.ticker || !refs.tickerFact) return;
      tickerIdx = Math.floor(Math.random() * TICKER_FACTS.length); // départ varié
      refs.tickerFact.textContent = TICKER_FACTS[tickerIdx];
      refs.ticker.hidden = false;
      refs.ticker.classList.add('cv-pc-in');
      tickerTimer = setInterval(function () {
        tickerIdx = (tickerIdx + 1) % TICKER_FACTS.length;
        swapText(refs.tickerFact, TICKER_FACTS[tickerIdx]);
      }, ROTATE_MS);
    }

    function foundEntrance() {
      if (foundEntranceDone) return;
      foundEntranceDone = true;
      if (!reduced && refs.foundCard) refs.foundCard.classList.add('cv-pc-found-in');
      var asideEl = root.querySelector('.cv-v2-precheck__aside');
      if (asideEl) {
        setTimeout(function () { asideEl.setAttribute('data-cv-pc-aside', 'ready'); }, reduced ? 0 : 400);
      }
    }

    /* --- Plate resolution: ?plate= → ?plaque= → sessionStorage cv_plate --- */

    var rawPlate = params.get('plate') || params.get('plaque') || '';
    if (!rawPlate) {
      try { rawPlate = window.sessionStorage.getItem('cv_plate') || ''; } catch (e) { /* ignore */ }
    }
    var norm = strip(rawPlate);

    /* --- Intent chips (optional, non-blocking) ---------------------------- */

    Array.prototype.forEach.call(refs.intentChips, function (chip) {
      chip.addEventListener('click', function () {
        var pressed = chip.getAttribute('aria-pressed') === 'true';
        Array.prototype.forEach.call(refs.intentChips, function (c) {
          c.setAttribute('aria-pressed', 'false');
        });
        if (!pressed) {
          chip.setAttribute('aria-pressed', 'true');
          intentValue = chip.getAttribute('data-cv-pc-intent-chip') || null;
        } else {
          intentValue = null;
        }
      });
    });

    /* --- Prefetch — head start for the live identification ------------------
       Fire-and-forget: queues a real scrape server-side when the plate is
       not cached. Its response is never read; the polling below is the only
       source of truth. Skipped entirely in mock mode. --------------------- */

    function firePrefetch(plateNorm) {
      if (mock) return; // QA hooks must never trigger a real identification
      try {
        var ctrl = ('AbortController' in window) ? new AbortController() : null;
        if (ctrl) setTimeout(function () { try { ctrl.abort(); } catch (e) {} }, PREFETCH_TIMEOUT_MS);
        fetch(PREFETCH_URL, {
          method: 'POST',
          mode: 'cors',
          keepalive: true,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ plate: plateNorm }),
          signal: ctrl ? ctrl.signal : undefined
        }).catch(function () { /* fire-and-forget */ });
      } catch (e) { /* never block the page */ }
    }

    /* --- Live lookup (row 1 is real) — one GET per poll --------------------- */

    function fetchLookupOnce(plateNorm) {
      if (mock === 'found') {
        return delay(1200).then(function () { return MOCK_FOUND; });
      }
      if (mock === 'generic') {
        return delay(900).then(function () { return { status: 'generic' }; });
      }
      if (mock === 'pending-found') {
        return delay(400).then(function () {
          return (performance.now() - startedAt >= 12000) ? MOCK_FOUND : { status: 'pending' };
        });
      }
      return new Promise(function (resolve) {
        var settled = false;
        function finish(res) {
          if (settled) return;
          settled = true;
          resolve(res);
        }
        var timer = setTimeout(function () { finish({ status: 'generic' }); }, LOOKUP_TIMEOUT_MS);
        try {
          var ctrl = ('AbortController' in window) ? new AbortController() : null;
          if (ctrl) setTimeout(function () { try { ctrl.abort(); } catch (e) {} }, LOOKUP_TIMEOUT_MS);
          fetch(LOOKUP_URL + '?plate=' + encodeURIComponent(plateNorm), {
            method: 'GET',
            mode: 'cors',
            signal: ctrl ? ctrl.signal : undefined
          })
            .then(function (r) { return r.ok ? r.json() : { status: 'generic' }; })
            .then(function (d) {
              clearTimeout(timer);
              finish((d && typeof d === 'object') ? d : { status: 'generic' });
            })
            .catch(function () { clearTimeout(timer); finish({ status: 'generic' }); });
        } catch (e) {
          clearTimeout(timer);
          finish({ status: 'generic' });
        }
      });
    }

    /* --- Adaptive polling — every ~4 s, up to 75 s total --------------------
       "found" settles immediately. "pending" keeps row 1 running. "generic"
       mid-poll is NOT final (the scrape may not be queued yet) — only the
       exhausted window settles to generic, quietly. ------------------------ */

    function pollLookup(plateNorm, handlers) {
      var stopped = false;
      var polls = 0;

      function nextDelay() {
        var d = polls < POLL_SCHEDULE_MS.length ? POLL_SCHEDULE_MS[polls] : POLL_INTERVAL_MS;
        polls += 1;
        return d;
      }

      function attempt() {
        if (stopped) return;
        fetchLookupOnce(plateNorm).then(function (d) {
          if (stopped) return;
          if (d && d.status === 'found' && (d.marque || d.modele)) {
            stopped = true;
            handlers.onFound(d);
            return;
          }
          if (d && d.status === 'pending') handlers.onPending();
          // mock=generic is a definitive answer — no reason to poll for 75 s
          var elapsed = performance.now() - startedAt;
          var wait = nextDelay();
          if (mock === 'generic' || elapsed + wait >= POLL_WINDOW_MS) {
            stopped = true;
            handlers.onEnd();
            return;
          }
          setTimeout(attempt, wait);
        });
      }

      attempt();
    }

    /* --- Lead beacon — fire-and-forget, never blocks checkout -------------- */

    function sendLead(email, plateCanonical) {
      var key = email + '|' + plateCanonical + '|' + (intentValue || '');
      if (key === lastLeadKey) return;
      lastLeadKey = key;
      try {
        var ctrl = ('AbortController' in window) ? new AbortController() : null;
        if (ctrl) setTimeout(function () { try { ctrl.abort(); } catch (e) {} }, LEAD_TIMEOUT_MS);
        fetch(LEAD_URL, {
          method: 'POST',
          mode: 'cors',
          keepalive: true,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: email,
            plate: plateCanonical,
            intent: intentValue,
            source: src
          }),
          signal: ctrl ? ctrl.signal : undefined
        }).catch(function () { /* fire-and-forget */ });
      } catch (e) { /* never block */ }
    }

    /* --- Checklist choreography -------------------------------------------- */

    function setRow(index, state, note, withTime) {
      var row = refs.rows[index];
      if (!row) return;
      var prev = row.getAttribute('data-cv-pc-state');
      row.setAttribute('data-cv-pc-state', state);
      if (note) {
        var noteEl = row.querySelector('[data-cv-pc-note]');
        if (noteEl) noteEl.textContent = note;
      }
      if (withTime) {
        var timeEl = row.querySelector('[data-cv-pc-time]');
        if (timeEl) timeEl.textContent = fmtElapsed(performance.now() - startedAt);
      }
      // Progress rail + "Bases couvertes n/6" — counts real done transitions only
      if (state === 'done' && prev !== 'done') {
        doneRows += 1;
        if (refs.listWrap) refs.listWrap.style.setProperty('--cv-pc-progress', String(doneRows / 6));
        if (refs.count && refs.countN) {
          refs.count.hidden = false;
          refs.countN.textContent = String(doneRows);
          if (!reduced) {
            refs.countN.classList.remove('cv-pc-tickup');
            void refs.countN.offsetWidth; // re-trigger
            refs.countN.classList.add('cv-pc-tickup');
          }
        }
      }
    }

    var COVERAGE_NOTE = 'couverture disponible pour ce véhicule';
    var ACTIVE_NOTE = 'contrôle de la couverture en cours';

    function finalizeStatus() {
      if (refs.statusText) refs.statusText.textContent = 'Vérification terminée';
      if (refs.statusTime) refs.statusTime.textContent = fmtElapsed(performance.now() - startedAt);
      if (refs.status) refs.status.setAttribute('data-cv-pc-phase', 'done');
    }

    function markPending() {
      if (pendingNoted || lookupState !== 'pending') return;
      pendingNoted = true;
      setRow(0, 'active', PENDING_NOTE);
      // Rotating process lines during the long wait (reduced: static note)
      if (!reduced && !pendingRotTimer && PENDING_LINES) {
        var noteEl = refs.rows[0] && refs.rows[0].querySelector('[data-cv-pc-note]');
        if (noteEl) {
          pendingLineIdx = 0;
          swapText(noteEl, PENDING_LINES[0]);
          pendingRotTimer = setInterval(function () {
            // after the first cycle, loop from L3 (never re-claim "connexion établie")
            pendingLineIdx = pendingLineIdx >= PENDING_LINES.length - 1 ? 2 : pendingLineIdx + 1;
            swapText(noteEl, PENDING_LINES[pendingLineIdx]);
          }, ROTATE_MS);
        }
      }
    }

    function handleFound(res) {
      if (lookupState !== 'pending') return;
      lookupState = 'found';
      foundRes = res;
      stopTheater();
      // Min display time so the row-1 tick is legible even on a cache hit.
      var minMs = reduced ? 120 : 1100;
      var wait = Math.max(0, minMs - (performance.now() - startedAt));
      delay(wait).then(function () {
        setRow(0, 'done', 'véhicule identifié', true);
        // "vérifié" badge — only when the vehicle is really identified
        if (refs.chipBadge) {
          refs.chipBadge.hidden = false;
          if (!reduced) refs.chipBadge.classList.add('cv-pc-badge-in');
        }
        if (resultShown) {
          finalizeStatus();
          upgradeToFound(res);
        }
        beaconSettled();
        requestReveal();
      });
    }

    function handleEnd() {
      if (lookupState !== 'pending') return;
      lookupState = 'generic';
      stopTheater();
      var minMs = reduced ? 120 : 1100;
      var wait = Math.max(0, minMs - (performance.now() - startedAt));
      delay(wait).then(function () {
        setRow(0, 'done', 'immatriculation prise en charge', true);
        if (resultShown) finalizeStatus();
        beaconSettled();
        requestReveal();
      });
    }

    function startRun(canonicalPlate) {
      var plateNorm = strip(canonicalPlate);

      try { window.sessionStorage.setItem('cv_plate', canonicalPlate); } catch (e) { /* ignore */ }

      if (refs.entry) refs.entry.hidden = true;
      if (refs.run) {
        refs.run.hidden = false;
        refs.run.classList.add('cv-pc-in');
      }
      if (refs.chipChars) refs.chipChars.textContent = canonicalPlate;
      if (refs.plateProp) refs.plateProp.value = canonicalPlate;
      if (refs.statusText) refs.statusText.textContent = 'Contrôle de couverture en cours';

      startedAt = performance.now();

      /* Dénominateur de l'A/B. Émis AVANT toute divergence de timing entre les
         bras : c'est le seul comptage comparable. `precheck_result`, lui, est
         émis à la révélation, donc plus tard dans le bras B — l'utiliser comme
         dénominateur surestime mécaniquement B. */
      if (!startBeaconed) {
        startBeaconed = true;
        beacon('precheck_start', { ab: arm });
      }

      /* Abandon pendant l'attente : la population qui tape sa plaque et part
         avant l'offre n'était mesurée nulle part. pagehide couvre la fermeture
         et la navigation, visibilitychange couvre le passage en arrière-plan
         sur mobile (72,5 % du trafic). */
      var noteExit = function () {
        if (exitBeaconed) return;
        exitBeaconed = true;
        var secs = Math.round((performance.now() - startedAt) / 1000);
        if (secs < 0) secs = 0;
        if (secs > 60) secs = 60;
        beacon('precheck_exit', {
          ab: arm,
          route: revealDone ? ('shown-' + lookupState) : ('waiting-' + lookupState),
          ms: String(secs)
        });
      };
      window.addEventListener('pagehide', noteExit);
      document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'hidden') noteExit();
      });

      // Rotating process lines for the long pending wait (coverage/process
      // only — never a finding). After one cycle, loops from L3.
      PENDING_LINES = [
        'Connexion au registre SIV établie',
        'Lecture des enregistrements pour ' + canonicalPlate,
        'Croisement des bases kilométrage',
        'Contrôle de la couverture sinistres et VGE',
        'Vérification des usages professionnels enregistrés',
        'Consultation des gages et oppositions',
        PENDING_NOTE
      ];

      // Stamp-in — the chip lands like a stamp (transform only)
      if (refs.chip && !reduced) refs.chip.classList.add('cv-pc-stamp');

      // Head start — queue the real identification before anything animates.
      firePrefetch(plateNorm);

      // Row 1 — live SIV interrogation
      setRow(0, 'active', 'identification du véhicule en cours');

      pollLookup(plateNorm, {
        onFound: handleFound,
        onPending: markPending,
        onEnd: handleEnd
      });

      // Rows 2-6 — coverage checks, choreographed
      var schedule = reduced
        ? [150, 250, 350, 450, 550]
        : [2200, 3200, 4100, 5000, 5900];

      schedule.forEach(function (t, i) {
        var rowIndex = i + 1;
        setTimeout(function () {
          setRow(rowIndex, 'active', ACTIVE_NOTE);
        }, Math.max(0, t - (reduced ? 100 : 750)));
        setTimeout(function () {
          setRow(rowIndex, 'done', COVERAGE_NOTE, true);
        }, t);
      });

      // Intent question appears early in the sequence
      setTimeout(function () {
        if (refs.intent) {
          refs.intent.hidden = false;
          refs.intent.classList.add('cv-pc-in');
        }
      }, reduced ? 120 : 1000);

      // Révélation de l'offre. Bras A : plancher fixe, comme avant. Bras B :
      // le plafond n'est qu'un filet — requestReveal() avance la révélation
      // dès que l'identification aboutit, pour que le visiteur voie SA voiture
      // avant qu'on lui demande d'acheter.
      revealPlate = canonicalPlate;
      revealFloorMs = reduced ? 800 : REVEAL_FLOOR_MS;
      revealCapMs = (reduced || arm === 'A') ? revealFloorMs : REVEAL_CAP_MS;
      revealTimer = setTimeout(function () { showResult(canonicalPlate); }, revealCapMs);

      /* Personne ne doit être retenu contre son gré. Dès le plancher, on offre
         la sortie explicite ; ceux qui veulent voir l'offre tout de suite la
         voient, ceux qui préfèrent attendre leur véhicule identifié attendent.
         Le bouton n'existe que dans le bras B (le bras A révèle au plancher, il
         n'a rien à attendre) et disparaît à la révélation. */
      if (arm === 'B' && !reduced && refs.run) {
        setTimeout(function () {
          if (revealDone || skipBtn) return;
          skipBtn = document.createElement('button');
          skipBtn.type = 'button';
          skipBtn.className = 'cv-pc-skip';
          skipBtn.textContent = 'Continuer sans attendre';
          skipBtn.style.cssText = 'display:block;margin:16px auto 0;padding:10px 18px;' +
            'font:inherit;font-size:14px;color:inherit;opacity:.72;background:transparent;' +
            'border:1px solid currentColor;border-radius:999px;cursor:pointer;';
          skipBtn.addEventListener('click', function () {
            var secs = Math.round((performance.now() - startedAt) / 1000);
            beacon('precheck_skip', { ab: arm, ms: String(secs > 60 ? 60 : secs) });
            showResult(canonicalPlate);
          });
          try { refs.run.appendChild(skipBtn); } catch (e) { /* ignore */ }
        }, revealFloorMs);
      }
    }

    function populateFound(res) {
      var marque = cleanField(res.marque, 30);
      var modele = cleanField(res.modele, 40);
      var annee = /^[0-9]{4}$/.test(String(res.annee || '')) ? String(res.annee) : '';
      var energie = cleanField(res.energie, 20);

      // Dedupe "MINI MINI"-style responses where modele repeats the marque
      var displayName = (marque + ' ' + modele).trim();
      if (marque && modele && marque.toUpperCase() === modele.toUpperCase()) {
        displayName = marque;
      }
      if (refs.vehicle) refs.vehicle.textContent = displayName;
      if (refs.vehicleMeta) {
        var meta = [annee, energie].filter(Boolean).join(' · ');
        refs.vehicleMeta.textContent = meta;
        refs.vehicleMeta.hidden = !meta;
      }

      // Marque logo: hit -> reveal, miss or load error -> stays hidden. The
      // card must never show a broken image.
      if (refs.logo) {
        var logoUrl = marque ? cvLogoUrl(marque) : '';
        if (logoUrl) {
          refs.logo.onerror = function () { this.hidden = true; };
          refs.logo.alt = marque;
          refs.logo.src = logoUrl;
          refs.logo.hidden = false;
        } else {
          refs.logo.hidden = true;
          refs.logo.removeAttribute('src');
        }
      }

      /* properties[Véhicule] — reaches the order so the delivery pipeline and
         the confirmation email name the car, not just the plate. Only ever
         written from a real "found" lookup (this function's two callers are
         both found-only); stays `disabled` otherwise so an unresolved plate
         never posts an empty or invented value. Never touches the plate. */
      if (refs.vehicleProp) {
        var vparts = [displayName];
        if (annee) vparts.push(annee);
        if (energie && energie.length <= 16) vparts.push(energie);
        var vval = vparts.filter(Boolean).join(' · ');
        if (displayName && vval) {
          refs.vehicleProp.value = vval;
          refs.vehicleProp.disabled = false;
        } else {
          refs.vehicleProp.value = '';
          refs.vehicleProp.disabled = true;
        }
      }
    }

    /* Generic card → "Véhicule identifié", in place. The result block's
       height is animated between the two card sizes so the email field and
       CTA below never jump under the visitor's focus or thumb. */
    function upgradeToFound(res) {
      if (!refs.foundCard || !refs.foundCard.hidden) return;
      populateFound(res);
      var wrap = refs.result;
      var h0 = (wrap && !reduced) ? wrap.offsetHeight : 0;
      refs.foundCard.hidden = false;
      if (refs.genericCard) refs.genericCard.hidden = true;
      if (wrap && !reduced) {
        var h1 = wrap.offsetHeight;
        if (h1 !== h0) {
          wrap.style.height = h0 + 'px';
          wrap.classList.add('cv-v2-precheck__result--swap');
          void wrap.offsetHeight; // reflow so the height transition runs
          wrap.style.height = h1 + 'px';
          setTimeout(function () {
            wrap.classList.remove('cv-v2-precheck__result--swap');
            wrap.style.height = '';
          }, 320);
        }
      }
      foundEntrance();
    }

    function showResult(canonicalPlate) {
      if (revealDone) return;
      revealDone = true;
      if (revealTimer) { clearTimeout(revealTimer); revealTimer = null; }
      if (skipBtn && skipBtn.parentNode) {
        try { skipBtn.parentNode.removeChild(skipBtn); } catch (e) { /* ignore */ }
        skipBtn = null;
      }
      resultShown = true;

      if (lookupState === 'found' && foundRes) {
        populateFound(foundRes);
        if (refs.foundCard) refs.foundCard.hidden = false;
        if (refs.genericCard) refs.genericCard.hidden = true;
        foundEntrance();
        finalizeStatus();
      } else {
        if (refs.genericPlate) refs.genericPlate.textContent = canonicalPlate;
        if (refs.genericCard) refs.genericCard.hidden = false;
        if (refs.foundCard) refs.foundCard.hidden = true;
        if (lookupState === 'generic') {
          finalizeStatus();
        } else {
          // Row 1 is still interrogating live — the card can still upgrade.
          if (refs.statusText) refs.statusText.textContent = 'Interrogation SIV en temps réel';
          markPending();
          startTicker();
        }
      }

      if (refs.result) {
        refs.result.hidden = false;
        refs.result.classList.add('cv-pc-in');
      }
      if (refs.buy) {
        refs.buy.hidden = false;
        refs.buy.classList.add('cv-pc-in');
        try {
          refs.buy.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'nearest' });
        } catch (e) { /* ignore */ }
      }

      // Funnel measurement: the offer (email + CTA) is now visible. Tagged by
      // src so the article/home plate-CTA → precheck → checkout path is finally
      // traceable end-to-end. Fire-and-forget, one-shot.
      if (!pcResultBeaconed) {
        pcResultBeaconed = true;
        beacon('precheck_result', { route: lookupState, ab: arm });
      }
    }

    /* --- Buy form wiring ---------------------------------------------------
       The actual /cart/add submit is owned by cv-v2-plate.js (cart clear →
       add → return_to). Everything here happens BEFORE the submit event:
       keep return_to in sync with the email (checkout prefill) and fire the
       lead beacon on an armed, valid submit. A submit while row 1 is still
       pending proceeds normally — the scrape keeps warming the cache. ----- */

    function syncReturnTo() {
      if (!refs.returnTo) return;
      var email = refs.email ? refs.email.value.trim() : '';
      if (email && refs.email.validity.valid) {
        refs.returnTo.value = '/checkout?checkout%5Bemail%5D=' + encodeURIComponent(email);
      } else {
        refs.returnTo.value = '/checkout';
      }
    }

    /* Pose une line-item property sur le formulaire d'achat. Le préfixe « _ »
       la garde invisible du client tout en la conservant sur la commande. */
    function setProp(form, key, value) {
      if (!form) return;
      var name = 'properties[' + key + ']';
      var el = form.querySelector('input[name="' + name + '"]');
      if (!el) {
        el = document.createElement('input');
        el.type = 'hidden';
        el.name = name;
        form.appendChild(el);
      }
      el.value = value;
    }

    function armSubmit() {
      var form = refs.buy;
      if (!form || !form.checkValidity()) return;
      syncReturnTo();
      // Rattacher la commande au bras A/B et à l'état d'identification : c'est
      // la seule façon de savoir si « véhicule identifié » fait vendre.
      setProp(form, '_ab_reveal', arm);
      setProp(form, '_pc_route', lookupState);
      // Le visiteur venu d'une page OBD a été tiré dans l'A/B plaque-vs-CarOccaz
      // sur la page précédente. cv-v2-plate.js ne transporte pas ce bras dans la
      // redirection, mais localStorage est partagé : on le relit ici, sinon
      // l'expérience OBD n'a aucune commande attribuable.
      try {
        var abObd = window.localStorage.getItem('cv_ab_obd');
        if (abObd === 'caroccaz' || abObd === 'plaque') setProp(form, '_ab_obd', abObd);
      } catch (e) { /* stockage refusé */ }
      beacon('precheck_buy', { route: lookupState, ab: arm });
      var plateCanonical = refs.plateProp ? refs.plateProp.value : '';
      // Email is optional: only fire the lead beacon when a valid address was given.
      var leadEmail = refs.email ? refs.email.value.trim() : '';
      if (leadEmail && refs.email.validity.valid) sendLead(leadEmail, plateCanonical);
    }

    if (refs.email) {
      refs.email.addEventListener('input', syncReturnTo);
      refs.email.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') armSubmit();
      });
    }
    if (refs.cta) refs.cta.addEventListener('click', armSubmit);

    /* --- Entry form (no plate yet) ----------------------------------------- */

    if (refs.entryForm) {
      refs.entryForm.addEventListener('submit', function (e) {
        e.preventDefault();
        var n = strip(refs.entryInput ? refs.entryInput.value : '');
        if (!isValidPlate(n)) {
          if (refs.entryInput) {
            refs.entryInput.setAttribute('data-cv-plate-state', 'invalid');
            refs.entryInput.classList.add('cv-v2-plate-shake');
            setTimeout(function () {
              refs.entryInput.classList.remove('cv-v2-plate-shake');
            }, 520);
            try { refs.entryInput.focus(); } catch (err) { /* ignore */ }
          }
          return;
        }
        startRun(canonical(n));
      });
    }

    /* --- "Modifier" — back to entry mode ----------------------------------- */

    if (refs.chipEdit) {
      refs.chipEdit.addEventListener('click', function () {
        try { window.sessionStorage.removeItem('cv_plate'); } catch (e) { /* ignore */ }
        var target = window.location.pathname;
        if (src && src !== 'precheck') target += '?src=' + encodeURIComponent(src);
        window.location.assign(target);
      });
    }

    /* --- Aside accordion — open on desktop, collapsed on mobile ------------ */

    var asideAcc = root.querySelector('[data-cv-pc-aside-acc]');
    if (asideAcc) {
      try {
        if (window.matchMedia('(min-width: 1040px)').matches) asideAcc.setAttribute('open', '');
      } catch (e) { asideAcc.setAttribute('open', ''); } // fallback: open
    }

    /* --- Mode selection ----------------------------------------------------- */

    if (norm && isValidPlate(norm)) {
      startRun(canonical(norm));
    } else {
      if (refs.entry) refs.entry.hidden = false;
      if (refs.run) refs.run.hidden = true;
      if (refs.entryInput && norm) {
        // Prefill whatever was passed, even if invalid — the visitor corrects it.
        refs.entryInput.value = rawPlate;
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();

